import { CartAnalytics } from "../analytics/analytics.types";
import { AddToCartRequestDto, BundlableCartItemDto, CartDto,  CartItemDto,  PriceWithAdjustmentsDto,  SessionService, UpdateCartItemRequestDto, } from "../api";
import { getOrderStore } from "../utils";

export class CartStore {
    private loadingStartTime: number | null = null;
    private storeId: string;

    public loading: boolean = false

    public subtotal!: PriceWithAdjustmentsDto;
    public items: BundlableCartItemDto[] = [];
    public id: string | undefined | null = null;

    constructor(
        storeId: string,
        bootstrapData: CartDto
    ) {
        this.storeId = storeId;

        this.updateState(bootstrapData);
    }

    init() {
        console.debug("CartStore initialized");
    }

    private async fetchCart() : Promise<boolean> {
        if(!this.storeId) {
            console.error("Store is not defined in CartStore");
        }
        const { data, error } = await SessionService.getCart({
            headers:{
                Store: this.storeId
            }
        })

        if(!error) {
            const newData = { ...data } as CartDto;
            newData.items ??= [];
            this.updateState(newData);
            window.dispatchEvent(new CustomEvent("cart-updated", { detail: { userUpdate: false } }));
            return true;
        }

        if (error) {
            window.dispatchEvent(new CustomEvent("cart-error", { detail: { error: error } }));
            return false;
        }
        return false;
    }

    public async RefreshCart() {
        this.loading = true;
        this.loadingStartTime = Date.now();
        if(await this.fetchCart()) {
            const elapsedTime = Date.now() - (this.loadingStartTime ?? 0);
            this.stopLoadingAfter(elapsedTime);
        }
        this.loading = false;
    }

    public async AddProduct(productData: AddToCartRequestDto, popupCart: boolean = true) {
        this.loading = true;
        this.loadingStartTime = Date.now();

        const { error } = await SessionService.postAddToCart({
            headers:{
                Store: this.storeId
            },
            body: productData
        });

        if(!error && await this.fetchCart()) {
            const elapsedTime = Date.now() - (this.loadingStartTime ?? 0);
            this.stopLoadingAfter(elapsedTime, new CustomEvent("cart-updated", { detail: { userUpdate: popupCart ? true : false } }));

            const addedItems = this.items.filter(i => i.productReference === productData.productReference);
            if(addedItems.length == 1) {
                const addedItem = addedItems[0];
                window.dispatchEvent(new CustomEvent<CartAnalytics>("cart-item-added", { detail: {
                    currency: getOrderStore().currencyCode,
                    value: Number(addedItem.unitPrice.value.withoutTax) * addedItem.quantity,
                    items: addedItems.map((value, index) => ({
                        item_id: value.sku,
                        item_name: value.name,
                        price: Number(value.unitPrice.value.withoutTax),
                        discount: Number(value.unitPrice.adjustment),
                        quantity: value.quantity,
                        index: index
                    }))
                }}));
            }
        }

        if (error) {
            this.loading = false;
            window.dispatchEvent(new CustomEvent("cart-error", { detail: { error: error } }));
            return;
        }
    }

    public async UpdateProduct(orderLineId: string, orderLineData: UpdateCartItemRequestDto, popupCart: boolean = true) {
        this.loading = true;
        this.loadingStartTime = Date.now();

        const { error } = await SessionService.putUpdateCartItem({
            headers:{
                Store: this.storeId
            },
            path:{
                id: orderLineId
            },
            body: orderLineData
        });

        if(!error && await this.fetchCart()) {
            const elapsedTime = Date.now() - (this.loadingStartTime ?? 0);
            this.stopLoadingAfter(elapsedTime, new CustomEvent("cart-updated", { detail: { userUpdate: popupCart ? true : false } }));
        }
        if (error) {
            this.loading = false;
            window.dispatchEvent(new CustomEvent("cart-error", { detail: { error: error } }));
            return;
        }
    }
    public async RemoveProduct(orderLineId: string, popupCart: boolean = true) {
        this.loading = true;
        this.loadingStartTime = Date.now();

        const removedOrderLine = this.FindOrderLine(orderLineId);

        const { error } = await SessionService.deleteCartItemById({
            headers:{
                Store: this.storeId
            },
            path:{
                id: orderLineId
            }
        });

        this.notifyPlausibleOfRemove();
        this.notifyGoogleAnalyticsOfRemove(orderLineId);

        if(!error && await this.fetchCart()) {
            const elapsedTime = Date.now() - (this.loadingStartTime ?? 0);
            this.stopLoadingAfter(elapsedTime, new CustomEvent("cart-updated", { detail: { userUpdate: popupCart ? true : false } }));

            if(removedOrderLine) {
                window.dispatchEvent(new CustomEvent<CartAnalytics>("cart-item-removed", { detail: {
                    currency: getOrderStore().currencyCode,
                    value: Number(removedOrderLine.total.value.withoutTax),
                    items: [removedOrderLine].map((value, index) => ({
                        item_id: value.sku,
                        item_name: value.name,
                        price: Number(value.unitPrice.value.withoutTax),
                        quantity: value.quantity,
                        index: index
                    }))
                }}));
            }
        }
        if (error) {
            this.loading = false;
            window.dispatchEvent(new CustomEvent("cart-error", { detail: { error: error } }));
            return;
        }

        if (error) {
            this.loading = false;
            window.dispatchEvent(new CustomEvent("cart-error", { detail: { error: error } }));
            return;
        }
    }
    public async ClearCart(silent: boolean = false) {
        this.loading = true;
        this.loadingStartTime = Date.now();

        const { error } = await SessionService.deleteClearCart({
            headers:{
                Store: this.storeId
            },
        })

        if(!error && await this.fetchCart()) {
            const elapsedTime = Date.now() - (this.loadingStartTime ?? 0);
            this.stopLoadingAfter(elapsedTime, new CustomEvent("cart-updated", { detail: { userUpdate: !silent } }));
        }

    }
    public FindOrderLine(orderLineId: string) : CartItemDto | BundlableCartItemDto | undefined {

        const bundlableItem = this.items?.find((item: BundlableCartItemDto) =>
            item.id === orderLineId ||
            item.items?.some((subItem: CartItemDto) => subItem.id === orderLineId)
        );

        const cartItem = bundlableItem?.id === orderLineId
            ? bundlableItem
            : bundlableItem?.items?.find((subItem: CartItemDto) => subItem.id === orderLineId);

        return cartItem;
    }

    private notifyGoogleAnalyticsOfRemove(orderLineId: string){
        const cartItem = this.FindOrderLine(orderLineId);

        if(!cartItem){
            return;
        }

        if((window as any).dataLayer){
            (window as any).dataLayer.push({ecommerce:null});
            (window as any).dataLayer.push({
                event: "remove_from_cart",
                ecommerce: {
                    items: [{
                        item_id: cartItem.productReference,
                        item_name: cartItem.name,
                        quantity: cartItem.quantity
                    }]
                }
            });
        }
    }
    private notifyPlausibleOfRemove(){
        if((window as any).plausible){
            (window as any).plausible("remove_cart_item");
        }
    }

    private stopLoadingAfter(elapsedTime: number, event: CustomEvent | null = null) {
        const minLoadingTime = 500; // Minimum loading time in milliseconds
        const remainingTime = minLoadingTime - elapsedTime;
        if(remainingTime > 0) {
            setTimeout(() => { this.loading = false; if (event) window.dispatchEvent(event); }, remainingTime);
        } else {
            this.loading = false;
            if (event) window.dispatchEvent(event);
        }
    }

    private updateState(cartData: CartDto) {
        this.id = cartData.id;
        this.items = cartData.items ?? [];
        this.subtotal = cartData.subtotal;
    }
}